import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ListOrdered } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** Id of a question's place in the thread (MessageList), for the outline to bring it back into view. */
export const questionAnchorId = (index: number) =>
    `assistant-question-${index}`;

type OutlineQuestion = {
    /** Index of the question in the thread. */
    index: number;
    text: string;
};

// Under two questions, there is nothing to find again.
const MIN_QUESTIONS = 2;

interface QuestionOutlineProps {
    questions: OutlineQuestion[];
}

const scrollToQuestion = (index: number) =>
    document
        .getElementById(questionAnchorId(index))
        ?.scrollIntoView({ behavior: "smooth", block: "start" });

/**
 * The conversation's questions, top left of the panel, like chapters: a small dash per question, the
 * one in view darker. On hover (or keyboard focus), the dashes open into the list of questions, only
 * then in the page; a click brings the question back into view.
 */
const QuestionOutline = ({ questions }: QuestionOutlineProps) => {
    const { t } = useTranslation();
    const [active, setActive] = useState<number | undefined>(undefined);
    const [open, setOpen] = useState(false);
    const indexes = questions.map((question) => question.index).join(",");

    // The question in view: the first one visible in the thread; none visible (a long answer), the
    // last one seen stays.
    useEffect(() => {
        if (typeof IntersectionObserver === "undefined") return undefined;
        const visible = new Set<number>();
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const index = Number(
                    (entry.target as HTMLElement).dataset.questionIndex,
                );
                if (entry.isIntersecting) visible.add(index);
                else visible.delete(index);
            });
            if (visible.size) setActive(Math.min(...visible));
        });
        questions.forEach((question) => {
            const anchor = document.getElementById(
                questionAnchorId(question.index),
            );
            if (anchor) observer.observe(anchor);
        });

        return () => observer.disconnect();
    }, [indexes]);

    if (questions.length < MIN_QUESTIONS) return null;

    const current = active ?? questions[questions.length - 1].index;
    const show = (index: number) => {
        scrollToQuestion(index);
        setActive(index);
    };

    return (
        <nav
            aria-label={t("vistassistant.outline.label")}
            className="absolute left-3 top-3 z-10 max-md:hidden"
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
            }}
            onFocus={() => setOpen(true)}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            {/* Closed: a dash per question. It stays under the open list, so focus is kept. */}
            <button
                aria-expanded={open}
                aria-label={t("vistassistant.outline.label")}
                className="flex max-h-[40vh] flex-col gap-1.5 overflow-hidden rounded-md px-1 py-1.5"
                type="button"
            >
                {questions.map((question) => (
                    <span
                        key={question.index}
                        className={cn(
                            "block h-px rounded-full",
                            question.index === current
                                ? "w-4 bg-primary-darkest"
                                : "w-3 bg-neutral-dark/40",
                        )}
                    />
                ))}
            </button>
            {open && (
                <ol className="absolute left-0 top-0 flex max-h-[60vh] w-[260px] flex-col overflow-y-auto rounded-xl border border-assistant-borderStrong bg-white p-1 shadow-[0_4px_16px_rgba(38,23,63,0.08)]">
                    {questions.map((question) => {
                        const isCurrent = question.index === current;

                        return (
                            <li key={question.index}>
                                <button
                                    aria-current={isCurrent || undefined}
                                    className={cn(
                                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors hover:bg-primary-lightest",
                                        isCurrent
                                            ? "font-medium text-primary-darkest"
                                            : "text-neutral-dark",
                                    )}
                                    onClick={() => show(question.index)}
                                    title={question.text}
                                    type="button"
                                >
                                    <span
                                        aria-hidden
                                        className={cn(
                                            "h-px flex-none rounded-full",
                                            isCurrent
                                                ? "w-3 bg-primary-darkest"
                                                : "w-2 bg-neutral-dark/40",
                                        )}
                                    />
                                    <span className="min-w-0 truncate">
                                        {question.text}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ol>
            )}
        </nav>
    );
};

/**
 * The same questions on a small screen, where the dashes would cover the thread: a button in the
 * header that opens their list (a tap, no hover on a phone).
 */
export const QuestionOutlineMenu = ({ questions }: QuestionOutlineProps) => {
    const { t } = useTranslation();

    if (questions.length < MIN_QUESTIONS) return null;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    aria-label={t("vistassistant.outline.label")}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-dark hover:bg-primary-lightest hover:text-primary-darkest"
                    title={t("vistassistant.outline.label")}
                    type="button"
                >
                    <ListOrdered
                        aria-hidden
                        className="size-4"
                        strokeWidth={1.75}
                    />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="max-h-[60vh] w-[260px] overflow-y-auto rounded-xl border-assistant-borderStrong p-1"
            >
                {questions.map((question) => (
                    <DropdownMenuItem
                        key={question.index}
                        className="rounded-md px-2 py-1.5 text-[13px] text-primary-darkest"
                        onSelect={() => scrollToQuestion(question.index)}
                    >
                        <span className="min-w-0 truncate">
                            {question.text}
                        </span>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default QuestionOutline;
